import { Button, CircularProgress, Typography } from '@mui/material'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useContext } from 'react'
import { allNavLinks } from '~/components/new/navigation/nav-links'
import { useNavItemVisibility } from '~/components/new/navigation/useNavItemVisible'
import { useFeatureFlagsReady } from '~/hooks/useFeatureFlag'
import { useRbacV2Enabled } from '~/utils/rbac'
import { AuthContext } from '../utils/context/auth.context'

export const Route = createFileRoute('/')({
  component: IndexComponentPage,
})

function IndexComponentPage() {
  const { authenticated, isLoading, hasRights, logout } = useContext(AuthContext)
  const v2Enabled = useRbacV2Enabled()
  const isNavItemVisible = useNavItemVisibility()
  const flagsReady = useFeatureFlagsReady()

  // La visibilité des entrées du menu dépend de leurs flags : tant que Flipt s'initialise, une
  // entrée flaggée est invisible et on atterrirait sur la mauvaise page (voire sur « Aucune page
  // accessible » pour un admin dont toutes les entrées sont flaggées).
  if (isLoading || !flagsReady) {
    return <CircularProgress />
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />
  }

  // Flag ON : la seule source de vérité est ce que l'admin peut réellement ouvrir. On atterrit sur
  // la première entrée visible du menu, dans l'ordre où le menu les affiche.
  if (v2Enabled) {
    const firstVisible = allNavLinks.find(isNavItemVisible)

    if (!firstVisible) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-sidebar px-6 py-10">
          <section className="w-full max-w-xl rounded-3xl border border-sidebar-border bg-background p-8 text-center shadow-xs">
            <Typography variant="h4" className="mb-3! font-semibold">
              Aucune page accessible
            </Typography>
            <Typography className="mb-6! text-muted-foreground">
              Votre compte n'a aucune permission permettant d'ouvrir une page du back-office.
              Rapprochez-vous d'un administrateur pour vous faire attribuer un rôle.
            </Typography>

            {/* Seule sortie : sans page accessible, toute navigation ramène ici. */}
            <Button variant="contained" onClick={logout}>
              Déconnexion
            </Button>
          </section>
        </main>
      )
    }

    return <Navigate {...firstVisible.linkOptions} replace />
  }

  if (hasRights(['READER'])) {
    return <Navigate to="/network-dashboard" replace />
  }
  if (hasRights(['STOCK_READER'])) {
    return <Navigate to="/stores" replace />
  }
  if (hasRights(['ACCOUNTING_READER'])) {
    return <Navigate to="/accounting" replace />
  }
  return <Navigate to="/network-dashboard" replace />
}
